import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import {
  previsualizarPlanProyeccion,
  aplicarPlanProyeccion,
  getPlanProyeccionActual,
  getPlanSnapshots,
  restaurarPlanSnapshot,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ClipboardPaste, Copy, CheckCircle2, AlertCircle, Loader2, Undo2, Play, Search,
} from "lucide-react";

// ---------------------------------------------------------------------------
// PLAN DE PROYECCIÓN pegable: las hipótesis como JSON (formato en
// docs/project-control/PLAN_PROYECCION.md). Flujo: pegar → Previsualizar
// (validación acción a acción, nada se escribe) → elegir qué aplicar →
// Aplicar (con snapshot automático) → Deshacer si hace falta.
// ---------------------------------------------------------------------------

export default function PlanContent({ empresaId, readOnly }) {
  const [texto, setTexto] = useState("");
  const [preview, setPreview] = useState(null);
  const [seleccion, setSeleccion] = useState({});      // codigo -> bool
  const [resultado, setResultado] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [ocupado, setOcupado] = useState(null);        // "preview"|"aplicar"|...
  const [confirmarRestaurar, setConfirmarRestaurar] = useState(null); // snapshot

  const cargarSnapshots = useCallback(async () => {
    try {
      const { data } = await getPlanSnapshots(empresaId);
      setSnapshots(data.snapshots || []);
    } catch {
      setSnapshots([]);
    }
  }, [empresaId]);

  useEffect(() => { cargarSnapshots(); }, [cargarSnapshots]);

  const parsear = () => {
    try {
      return JSON.parse(texto);
    } catch (e) {
      toast.error(`El plan no es JSON válido: ${e.message}`);
      return null;
    }
  };

  const handlePrevisualizar = async () => {
    const plan = parsear();
    if (!plan) return;
    setOcupado("preview");
    setResultado(null);
    try {
      const { data } = await previsualizarPlanProyeccion(empresaId, plan);
      setPreview(data);
      // Preselección: las acciones válidas y no desactivadas en el propio plan.
      const sel = {};
      (data.acciones || []).forEach((a) => { sel[a.codigo] = a.ok && a.aplicar; });
      setSeleccion(sel);
      if (data.ok) toast.success(`Plan válido: ${data.n_ok} acciones listas`);
      else toast.warning(`${data.n_error} acción(es) con error — revisa la tabla`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al previsualizar el plan");
    } finally {
      setOcupado(null);
    }
  };

  const handleAplicar = async () => {
    const plan = parsear();
    if (!plan || !preview) return;
    const solo = Object.entries(seleccion).filter(([, v]) => v).map(([k]) => k);
    if (solo.length === 0) {
      toast.error("No hay ninguna acción seleccionada");
      return;
    }
    setOcupado("aplicar");
    try {
      const { data } = await aplicarPlanProyeccion(empresaId, plan, solo);
      setResultado(data);
      await cargarSnapshots();
      if (data.errores > 0) {
        toast.warning(`Aplicadas ${data.aplicadas}, con ${data.errores} error(es)`);
      } else {
        toast.success(`Plan aplicado: ${data.aplicadas} acciones (deshacer disponible)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al aplicar el plan");
    } finally {
      setOcupado(null);
    }
  };

  const handleCopiarActual = async () => {
    setOcupado("volcado");
    try {
      const { data } = await getPlanProyeccionActual(empresaId);
      const json = JSON.stringify(data.plan, null, 2);
      setTexto(json);
      setPreview(null);
      setResultado(null);
      try {
        await navigator.clipboard.writeText(json);
        toast.success("Plan actual cargado en el editor y copiado al portapapeles");
      } catch {
        toast.success("Plan actual cargado en el editor");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al volcar el plan actual");
    } finally {
      setOcupado(null);
    }
  };

  const handleRestaurar = async () => {
    const snap = confirmarRestaurar;
    if (!snap) return;
    setOcupado("restaurar");
    try {
      await restaurarPlanSnapshot(empresaId, snap.id);
      setConfirmarRestaurar(null);
      setResultado(null);
      setPreview(null);
      await cargarSnapshots();
      toast.success("Configuración restaurada (el propio deshacer también se puede deshacer)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al restaurar");
    } finally {
      setOcupado(null);
    }
  };

  const filaEstado = (a) => {
    if (!a.ok) return <Badge variant="outline" className="text-rose-600 border-rose-400">✗ error</Badge>;
    if (!a.aplicar) return <Badge variant="outline" className="text-muted-foreground">pausada en el plan</Badge>;
    return <Badge variant="outline" className="text-emerald-600 border-emerald-400">✓ válida</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Editor */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <ClipboardPaste className="h-4 w-4 text-primary" />
            Plan de proyección (pegar y aplicar)
          </CardTitle>
          <CardDescription>
            Pega el plan en JSON (formato: <span className="font-mono">PLAN_PROYECCION.md</span>;
            códigos CFG / DRV / FIN / CAPEX / EVT). Nada se escribe hasta Aplicar, y todo
            se puede deshacer: antes de aplicar se guarda una copia automática.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <textarea
            value={texto}
            onChange={(e) => { setTexto(e.target.value); setPreview(null); }}
            placeholder='{ "version": 1, "nombre": "…", "config": { … }, "drivers": [ … ], "operaciones": [ … ] }'
            spellCheck={false}
            className="w-full h-64 rounded-md border bg-background p-3 font-mono text-xs resize-y"
            data-testid="plan-textarea"
          />
          <div className="flex flex-wrap gap-2">
            <Button onClick={handlePrevisualizar} disabled={!texto.trim() || !!ocupado} data-testid="plan-previsualizar">
              {ocupado === "preview" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Previsualizar
            </Button>
            <Button
              onClick={handleAplicar}
              disabled={readOnly || !preview || !!ocupado}
              variant={preview?.ok ? "default" : "secondary"}
              data-testid="plan-aplicar"
            >
              {ocupado === "aplicar" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              Aplicar seleccionadas
            </Button>
            <Button variant="outline" onClick={handleCopiarActual} disabled={!!ocupado} data-testid="plan-copiar-actual">
              {ocupado === "volcado" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
              Copiar plan actual
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Previsualización */}
      {preview && (
        <Card data-testid="plan-preview-card">
          <CardHeader className="pb-3">
            <CardTitle>
              Previsualización — {preview.n_ok} válidas · {preview.n_error} con error
            </CardTitle>
            <CardDescription>
              Años proyectados: {(preview.anios_proyectados || []).join(", ")}. Marca qué
              acciones aplicar (por defecto, las válidas no pausadas).
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="w-[190px]">Código</TableHead>
                  <TableHead>Acción</TableHead>
                  <TableHead className="w-[150px]">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(preview.acciones || []).map((a) => (
                  <TableRow key={a.codigo} className="hover:bg-muted/20">
                    <TableCell>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={!!seleccion[a.codigo]}
                        disabled={!a.ok}
                        onChange={(e) =>
                          setSeleccion((s) => ({ ...s, [a.codigo]: e.target.checked }))
                        }
                        data-testid={`plan-check-${a.codigo}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{a.codigo}</TableCell>
                    <TableCell className="text-xs">
                      {a.descripcion || "—"}
                      {a.nota && <p className="text-muted-foreground italic">{a.nota}</p>}
                      {a.error && <p className="text-rose-600">{a.error}</p>}
                    </TableCell>
                    <TableCell>{filaEstado(a)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Resultado de aplicar */}
      {resultado && (
        <Alert className={resultado.errores > 0
          ? "border-amber-300 bg-amber-50 dark:bg-amber-950/40"
          : "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40"}>
          {resultado.errores > 0
            ? <AlertCircle className="h-4 w-4 text-amber-600" />
            : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          <AlertTitle>
            {resultado.aplicadas} aplicadas · {resultado.omitidas} omitidas · {resultado.errores} errores
          </AlertTitle>
          <AlertDescription className="space-y-1">
            {resultado.resultados.filter((r) => r.ok === false).map((r) => (
              <p key={r.codigo} className="text-xs text-rose-600">
                <span className="font-mono">{r.codigo}</span>: {r.error}
              </p>
            ))}
            <p className="text-xs">
              Copia de seguridad creada — puedes deshacerlo desde el historial de abajo.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Deshacer */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Undo2 className="h-4 w-4 text-primary" />
            Deshacer (copias de seguridad)
          </CardTitle>
          <CardDescription>
            Antes de cada Aplicar (y de cada restauración) se guarda el estado completo.
            Restaurar deja la proyección EXACTAMENTE como estaba en ese momento.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {snapshots.length === 0 ? (
            <p className="text-sm text-muted-foreground">Todavía no hay copias.</p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead>Fecha</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead className="text-right">Drivers</TableHead>
                    <TableHead className="text-right">Operaciones</TableHead>
                    <TableHead className="w-[130px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {snapshots.map((s) => (
                    <TableRow key={s.id} className="hover:bg-muted/20">
                      <TableCell className="text-xs whitespace-nowrap">
                        {new Date(s.fecha).toLocaleString("es-ES")}
                      </TableCell>
                      <TableCell className="text-xs max-w-[280px] truncate">{s.motivo}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{s.resumen?.drivers ?? "—"}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{s.resumen?.operaciones ?? "—"}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={readOnly || !!ocupado}
                          onClick={() => setConfirmarRestaurar(s)}
                          data-testid={`plan-restaurar-${s.id}`}
                        >
                          <Undo2 className="h-3.5 w-3.5 mr-1" />
                          Restaurar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmación de restauración */}
      <AlertDialog open={!!confirmarRestaurar} onOpenChange={(o) => !o && setConfirmarRestaurar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Restaurar esta copia?</AlertDialogTitle>
            <AlertDialogDescription>
              La configuración de proyección (parámetros, drivers y operaciones) volverá
              EXACTAMENTE al estado de{" "}
              {confirmarRestaurar && new Date(confirmarRestaurar.fecha).toLocaleString("es-ES")}.
              Antes de restaurar se guarda otra copia del estado actual, así que este paso
              también se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={ocupado === "restaurar"}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleRestaurar(); }}
              disabled={ocupado === "restaurar"}
            >
              {ocupado === "restaurar" && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Restaurar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
